
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import CardBasicInfo from './CardBasicInfo';
import CardLocation from './CardLocation';
import CardStats from './CardStats';
import CardInsights from './CardInsights';
import CardLinkedPolls from './CardLinkedPolls';
import { CardItem } from '../CardForm';
import { useCategories } from '@/hooks/useCategories';

interface CardFormContentProps {
  currentCard: CardItem;
  setCurrentCard: (card: CardItem) => void;
  isEditing: boolean;
}

const CardFormContent: React.FC<CardFormContentProps> = ({
  currentCard,
  setCurrentCard,
  isEditing
}) => {
  const [linkedPolls, setLinkedPolls] = useState<any[]>([]);
  const [isLoadingPolls, setIsLoadingPolls] = useState(false);
  const [isUsingAutomaticStats, setIsUsingAutomaticStats] = useState(false);
  const [availablePolls, setAvailablePolls] = useState<any[]>([]);
  const { categories } = useCategories(true);
  
  useEffect(() => {
    // Only fetch linked polls when editing an existing card
    if (isEditing && currentCard) {
      fetchLinkedPolls(currentCard.id);
    }
    
    // Always fetch available polls that could be linked to this card
    fetchAvailablePolls();
  }, [isEditing, currentCard]);

  const fetchLinkedPolls = async (cardId: string) => {
    try {
      setIsLoadingPolls(true);
      
      const { data, error } = await supabase
        .from('poll_card_links')
        .select('poll_id')
        .eq('card_id', cardId);
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Get the linked poll IDs
        const pollIds = data.map(link => link.poll_id);
        
        // Fetch the actual polls
        const { data: pollsData, error: pollsError } = await supabase
          .from('polls')
          .select('*')
          .in('id', pollIds);
          
        if (pollsError) throw pollsError;
        
        setLinkedPolls(pollsData || []);
        
        // If we have linked polls, show a toast to let the user know
        if (pollsData && pollsData.length > 0) {
          toast.info(`This card is linked to ${pollsData.length} poll(s). Statistics can be calculated automatically.`);
          setIsUsingAutomaticStats(true);
        }
      } else {
        setLinkedPolls([]);
      }
    } catch (error) {
      console.error('Error fetching linked polls:', error);
    } finally {
      setIsLoadingPolls(false);
    }
  };
  
  const fetchAvailablePolls = async () => {
    try {
      const { data, error } = await supabase
        .from('polls')
        .select('*')
        .eq('status', 'live');
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        setAvailablePolls(data);
      }
    } catch (error) {
      console.error('Error fetching available polls:', error);
    }
  };

  const handleCategoryChange = (value: string) => {
    // Find corresponding category_id for the selected category name
    const selectedCategory = categories.find(cat => cat.name === value);
    
    setCurrentCard({
      ...currentCard,
      category: value,
      category_id: selectedCategory?.id || null
    });
  };

  const calculateStatsFromPolls = () => {
    if (linkedPolls.length === 0) return;
    
    try {
      let totalVotes = 0;
      let agreeVotes = 0;
      
      linkedPolls.forEach(poll => {
        const questions = poll.questions;
        
        if (questions && questions.options && questions.total_votes) {
          totalVotes += questions.total_votes;
          
          // Assuming the first option (index 0) is the "Agree" option
          if (questions.options.length > 0) {
            agreeVotes += questions.options[0].votes || 0;
          }
        }
      });
      
      // Calculate percentage
      const agreePercentage = totalVotes > 0 
        ? Math.round((agreeVotes / totalVotes) * 100) 
        : 0;
      
      // Update the card state
      setCurrentCard({
        ...currentCard,
        agreePercentage,
        totalPollsters: totalVotes
      });
      
      toast.success('Statistics calculated from linked polls');
    } catch (error) {
      console.error('Error calculating stats:', error);
      toast.error('Failed to calculate statistics');
    }
  };

  return (
    <div className="grid gap-4">
      <CardBasicInfo 
        title={currentCard.title}
        onTitleChange={(value) => setCurrentCard({ ...currentCard, title: value })}
        category={currentCard.category}
        onCategoryChange={handleCategoryChange}
        status={currentCard.status}
        onStatusChange={(value) => setCurrentCard({ ...currentCard, status: value })}
        description={currentCard.description}
        onDescriptionChange={(value) => setCurrentCard({ ...currentCard, description: value })}
      />
      
      <CardLocation 
        countryCode={currentCard.country_code}
        onCountryChange={(value) => setCurrentCard({ ...currentCard, country_code: value })}
        constituencyCode={currentCard.constituency_code}
        onConstituencyChange={(value) => setCurrentCard({ ...currentCard, constituency_code: value })}
      />
      
      <CardLinkedPolls
        linkedPolls={linkedPolls}
        isEditing={isEditing}
        availablePolls={availablePolls}
        isUsingAutomaticStats={isUsingAutomaticStats}
        isLoadingPolls={isLoadingPolls}
        calculateStatsFromPolls={calculateStatsFromPolls}
      />
      
      <CardStats 
        agreePercentage={currentCard.agreePercentage}
        onAgreePercentageChange={(value) => setCurrentCard({ ...currentCard, agreePercentage: value })}
        totalPollsters={currentCard.totalPollsters}
        onTotalPollstersChange={(value) => setCurrentCard({ ...currentCard, totalPollsters: value })}
        isReadOnly={isUsingAutomaticStats}
      />
      
      <CardInsights 
        insights={currentCard.insights || []}
        onChange={(insights) => setCurrentCard({ ...currentCard, insights })}
      />
    </div>
  );
};

export default CardFormContent;
